import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import I18n from 'I18n';
import { useMemo, useState } from 'react';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIProgress from 'UIComponents/progress/UIProgress';
import { PlaylistAssignmentsProgressContainer, useFilteredPlaylistAssignmentsForPortal } from '..';
import { FormattedDateTime, FormattedMessage } from '../../../components/I18n';
import styled, { theme } from '../../../utils/Styling';
import { TABLE_PAGINATOR_SIZE, Table } from '../../table';
import UIButton from 'UIComponents/button/UIButton';
import { useFullPlaylist } from '../../savedItems';
import UIFlex from 'UIComponents/layout/UIFlex';
import { UserLink } from '../../users';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import { AvatarViewer } from '../../avatar';
import formatName from 'I18n/utils/formatName';
const PlaylistItemHeader = styled.th.withConfig({
  displayName: "PlaylistAssignmentsForPortalTable__PlaylistItemHeader",
  componentId: "sc-1pey585-0"
})(["min-width:200px;"]);

const PlaylistAssignmentsForPortalTableHeaderActions = ({
  selectedRowsCount
}) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("small", {
      style: {
        textTransform: 'none',
        fontWeight: 400
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `learningCenter.playlistAssignments.assignmentsSelected`,
        options: {
          count: selectedRowsCount
        }
      })
    }), /*#__PURE__*/_jsxs(UIList, {
      childClassName: "m-left-3",
      inline: true,
      children: [/*#__PURE__*/_jsxs(UILink, {
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "delete"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.common.unassign"
        })]
      }), /*#__PURE__*/_jsxs(UILink, {
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "add"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.playlistAssignments.addDueDates"
        })]
      })]
    })]
  });
};

const PlaylistAssignmentsForPortalTableHeader = ({
  playlistId
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  if (!playlist) return null;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("th", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlistAssignments.columns.user"
      })
    }), /*#__PURE__*/_jsx("th", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlistAssignments.columns.dueDate"
      })
    }), playlist.items.map(item => /*#__PURE__*/_jsx(PlaylistItemHeader, {
      children: /*#__PURE__*/_jsx(UITruncateString, {
        matchContentWidth: true,
        children: item.name
      })
    }, item.itemId))]
  });
};

const PlaylistAssignmentsForPortalTableRow = ({
  assignee: {
    firstName,
    lastName,
    userId
  },
  dueDate,
  playlistId,
  portalId,
  progress: {
    progressPercentage,
    itemsProgress: allItemsProgress
  }
}) => {
  const displayName = formatName({
    firstName,
    lastName
  });
  const {
    playlist
  } = useFullPlaylist(playlistId);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        gap: 24,
        children: [/*#__PURE__*/_jsx(UserLink, {
          target: "_blank",
          userId: userId,
          children: /*#__PURE__*/_jsx(AvatarViewer, {
            displayName: displayName // src={avatarHash && getAvatarUrl(avatarHash)}
            ,
            size: "md",
            type: "contact"
          })
        }), /*#__PURE__*/_jsxs(PlaylistAssignmentsProgressContainer, {
          children: [/*#__PURE__*/_jsx(UserLink, {
            external: true,
            userId: userId,
            children: displayName
          }), /*#__PURE__*/_jsx(UIProgress, {
            "aria-label": I18n.text('learningCenter.playlistAssignments.assignmentProgress'),
            use: "success",
            value: progressPercentage
          })]
        })]
      })
    }), /*#__PURE__*/_jsx("td", {
      children: /*#__PURE__*/_jsx("div", {
        style: {
          fontWeight: 500
        },
        children: /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          children: dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
            format: "L",
            value: dueDate
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.common.add"
          })
        })
      })
    }), playlist && playlist.items.map(({
      itemId
    }) => {
      const itemProgress = allItemsProgress.find(progress => progress.itemId === itemId);
      const key = `${portalId}_${userId}_${itemId}`;
      if (!itemProgress || itemProgress.status === 'NOT_STARTED') return /*#__PURE__*/_jsx("td", {}, key);
      if (itemProgress.status === 'COMPLETED') return /*#__PURE__*/_jsx("td", {
        children: /*#__PURE__*/_jsx(UIIcon, {
          color: theme.colors.CALYPSO_DARK,
          name: "success"
        })
      }, key);
      return /*#__PURE__*/_jsx("td", {
        style: {
          fontWeight: 500
        },
        children: "TODO%"
      }, key);
    })]
  });
};

const PlaylistAssignmentsForPortalTable = ({
  playlistId,
  portalId
}) => {
  const [page, setPage] = useState(1);
  const {
    playlistAssignmentsForPortal,
    pageCount
  } = useFilteredPlaylistAssignmentsForPortal({
    playlistId,
    portalId,
    page
  });
  const rows = useMemo(() => {
    const assignmentsRecord = {};
    if (playlistAssignmentsForPortal) playlistAssignmentsForPortal.forEach(assignment => {
      assignmentsRecord[assignment.assignee.userId] = assignment;
    });
    return assignmentsRecord;
  }, [playlistAssignmentsForPortal]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Table, {
      renderHeader: () => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableHeader, {
        playlistId: playlistId
      }),
      renderHeaderActions: selectedRowsCount => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableHeaderActions, {
        selectedRowsCount: selectedRowsCount
      }),
      renderRow: data => /*#__PURE__*/_jsx(PlaylistAssignmentsForPortalTableRow, Object.assign({}, data)),
      rows: rows
    }), pageCount > 1 && /*#__PURE__*/_jsx(UIPaginator, {
      maxVisiblePageButtons: TABLE_PAGINATOR_SIZE,
      onPageChange: ({
        target: {
          value
        }
      }) => setPage(value),
      page: page,
      pageCount: pageCount
    })]
  });
};

export default PlaylistAssignmentsForPortalTable;